import { SubmissionsService } from './../../../../../services/submissions.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, OnDestroy, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { LanguageService } from '../../../services/language.service';
import { TranslateComponent } from 'src/app/sharedModule/translate/translate.component';
import { ActivatedRoute } from '@angular/router';
import { SessionService, SharingContext, ShareDetails } from '../../../../../services/session.service';
import { HttpClient } from '@angular/common/http';

interface ShareAccess {
  id: string;
  email: string;
  accessType: 'unlimited' | 'one_time' | 'time_based';
  accessPermission: 'view' | 'edit';
  accessTypeDisplay: string;
  shareType: 'email_share' | 'link_share';
  status: string;
  accessCount: number;
  isRevoked: boolean;
  isUsed: boolean;
  isExpired: boolean;
  emailDelivered?: boolean;
  createdAt: string;
  expiresAt?: string;
}

interface OtherShareInfo {
  shareType: 'email_share' | 'link_share';
  accessType: 'unlimited' | 'one_time' | 'time_based';
  accessPermission: 'view' | 'edit';
  sharedBy: string;
  shareCreatedAt: string;
  recipientInfo: string;
}

/**
 * @title Dialog with header, scrollable content and actions
 */
@Component({
  selector: 'privacy-settings-modal',
  templateUrl: './privacy-settings-modal.component.html',
  styleUrls: ['./privacy-settings-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivacySettingsModalComponent implements OnDestroy {
  userInfo: any;
  permissions: any;
  isDocumentInArchive: any;
  sourcesAreDeleted: any;
  submissionUserId: any;
  modalOpen;
  loggedInUser: any;
  isOnIA: boolean;
  isNotStudentPrecheck: any;
  // Sharing context properties
  sharingContext: SharingContext | null = null;
  isSharedSession: boolean = false;
  isViewOnlySession: boolean = false;
  userRole: 'owner' | 'recipient' | null = null;

  // Share Report properties
  shareMethod: 'email' | 'link' | null = null;
  accessType: 'unlimited' | 'one_time' | 'time_based' = 'one_time';
  accessPermission: 'view' | 'edit' = 'view';
  recipientEmail: string = '';
  shareMessage: string = '';
  timeBasedDuration: number = 3600; // 1 hour default
  timeBasedEndDate: string = '';
  isLoadingShare: boolean = false;
  shareAccesses: ShareAccess[] = [];

  // Share details for the current recipient
  currentShareDetails: ShareDetails | null = null;

  // Other shares for the same document
  othersWithAccess: OtherShareInfo[] = [];

  // Current time display and duration calculation
  currentTime: string = '';
  private timeInterval: any;

  // Expandable row management
  expandedRows: Set<string> = new Set();
  expandedSharers: Set<string> = new Set();

  // Others table toggle management
  isOthersTableExpanded: boolean = false;

  // Generated link management
  generatedShareLink: string = '';
  linkCopyMessage: string = '';
  isPrecheckShared: boolean = false;
  hasEditQueryParam: boolean = false;
  isStudentsDocument: boolean = false;

  isPrivacyDataLoaded: boolean = false;

  constructor(
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data,
    private acRouter: ActivatedRoute,
    public translate: TranslateService,
    private submissionsService: SubmissionsService,
    private spinner: NgxSpinnerService,
    private languageService: LanguageService,
    private sessionService: SessionService,
    private http: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    console.log(data, 'privacy settings modal data');

    this.userInfo = data.userInfo;
    this.loggedInUser = JSON.parse(localStorage.getItem('user'))
    this.permissions = data.studentDeletionPermissions;
    this.isDocumentInArchive = data.isDocumentInArchive;
    this.sourcesAreDeleted = data.sourcesAreDeleted;
    this.submissionUserId = data.submission.userId;
    this.isNotStudentPrecheck = data.isNotStudentPrecheck;
    // Initialize sharing context data
    this.initializeSharingContext();
  }

  ngOnInit(): void {
    this.modalOpen = true;
    this.languageService.selectedLanguage$.subscribe((language: string) => {
      this.translate.use(language);
    });

    if (this.acRouter.snapshot.params.clientName) {
      this.isOnIA = true;
    } else {
      this.isOnIA = false;
    }
    // Start real-time clock
    this.startClock();

    // Load data after component initialization
    setTimeout(() => {
      // Load existing shares for owners
      this.loadExistingShares();

      // Load received shares if user is a recipient
      if (this.userRole === 'recipient') {
        this.loadReceivedShares();
      }
    }, 0);

    this.isPrecheckShared = this.sharingContext?.isPrecheckShared || false;

    // Check for edit query parameter
    this.hasEditQueryParam = this.acRouter.snapshot.queryParams.edit === 'true';

    this.isStudentsDocument = (this.data?.submission?.user?.roleId == 5 || this.data?.submission?.user?.roleId == 9) && !this.hasEditQueryParam;
    console.log(this.isStudentsDocument, 'isStudentsDocument');

    // Set privacy data loading state based on session type
    if (this.isSharedSession) {
      this.isPrivacyDataLoaded = false; // Wait for async data in shared sessions
    } else {
      this.isPrivacyDataLoaded = true;  // Show immediately for normal sessions
    }
  }

  ngOnDestroy(): void {
    // Clean up the interval
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  /**
   * Start the real-time clock that updates every second
   */
  private startClock(): void {
    this.updateCurrentTime();

    // Run the interval outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.timeInterval = setInterval(() => {
        // Update the time
        this.updateCurrentTime();

        // Trigger change detection manually inside Angular zone
        this.ngZone.run(() => {
          this.changeDetectorRef.detectChanges();
        });
      }, 1000); // Update every 1000ms (1 second)
    });
  }

  /**
   * Update current time display
   */
  private updateCurrentTime(): void {
    const now = new Date();

    // Format to match datetime-local input format (YYYY-MM-DDTHH:MM:SS)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    this.currentTime = `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Initialize sharing context data from session service
   */
  private initializeSharingContext(): void {
    this.sharingContext = this.sessionService.getSharingContext();
    this.isSharedSession = this.sessionService.isSharedSession();
    this.isViewOnlySession = this.sessionService.isViewOnlySession();
    this.userRole = this.sessionService.getUserRole();
  }

  /**
   * Load existing shares for this report
   */
  private loadExistingShares(): void {
    // Only load shares if user is the owner (can manage shares)
    if (!this.canPerformActions() || this.userRole === 'recipient') {
      this.shareAccesses = [];
      setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      return;
    }

    // Load shares from backend using the correct endpoint
    const submissionId = this.data.submission.id;

    this.http.get<any>(`${environment.apiUrl}/submissions/${submissionId}/shares`).subscribe({
      next: (response) => {
        // Handle the nested response structure
        const sharesArray = response.shares || [];
        this.shareAccesses = sharesArray.map(share => ({
          id: share.id,
          email: share.recipientEmail || 'Link Share',
          accessType: share.accessType,
          accessPermission: share.accessPermission || 'view',
          accessTypeDisplay: this.getAccessTypeDisplay(share.accessType),
          shareType: share.shareType,
          status: share.status,
          accessCount: share.accessCount,
          isRevoked: share.isRevoked,
          isUsed: share.isUsed,
          isExpired: share.isExpired,
          emailDelivered: share.emailDelivered,
          createdAt: share.shareCreatedAt,
          expiresAt: share.expiresAt
        }));

        // Manually trigger change detection
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      },
      error: (error) => {
        console.error('❌ Failed to load existing shares:', error);
        // Set empty array on error, don't show error to user for this
        this.shareAccesses = [];
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      }
    });
  }

  /**
   * Load share details for recipients using the session ID
   * This shows the current share details and others with access
   */
  private loadReceivedShares(): void {
    // Get the current session ID from the sharing context
    const sessionId = localStorage.getItem('SESSION_ID');

    if (!sessionId) {
      console.warn('No session ID found for loading share details');
      this.currentShareDetails = null;
      this.othersWithAccess = [];

      this.isPrivacyDataLoaded = true;

      setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      return;
    }

    // Use the session service to get share details
    this.sessionService.getShareDetails(sessionId).subscribe({
      next: (response: ShareDetails) => {
        // Store the full share details
        this.currentShareDetails = response;

        // Store others with access
        this.othersWithAccess = response.othersWithAccess.shares || [];

        this.isPrivacyDataLoaded = true;

        // Manually trigger change detection
        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      },
      error: (error) => {
        console.error('❌ Failed to load share details:', error);

        if (error.status === 404) {
          console.error('Share not found');
        } else if (error.status === 403) {
          console.error('Access denied to share details');
        }

        this.currentShareDetails = null;
        this.othersWithAccess = [];

        this.isPrivacyDataLoaded = true;

        setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
      }
    });
  }



  /**
   * Set share method (email or link) - allows deselecting
   */
  setShareMethod(method: 'email' | 'link'): void {
    // If clicking the same method, deselect it
    if (this.shareMethod === method) {
      this.shareMethod = null;
    } else {
      this.shareMethod = method;
    }

    // Clear generated link when switching methods
    this.generatedShareLink = '';
    this.linkCopyMessage = '';

    // Reset form fields when switching
    this.recipientEmail = '';
    this.shareMessage = '';
    this.accessType = 'one_time';
    this.timeBasedEndDate = '';
    this.timeBasedDuration = 3600;
  }

  /**
   * Clear share method selection
   */
  clearShareMethod(): void {
    this.shareMethod = null;

    // Clear generated link
    this.generatedShareLink = '';
    this.linkCopyMessage = '';

    // Reset all form fields
    this.recipientEmail = '';
    this.shareMessage = '';
    this.accessType = 'one_time';
    this.timeBasedEndDate = '';
    this.timeBasedDuration = 3600;
  }

  /**
   * Check if email is valid for enabling next steps
   */
  get isEmailValid(): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return this.recipientEmail.trim() !== '' && emailRegex.test(this.recipientEmail.trim());
  }

  /**
   * Check if access type dropdown should be enabled
   */
  get isAccessTypeEnabled(): boolean {
    return this.shareMethod === 'email' ? this.isEmailValid : true;
  }

  /**
   * Check if time-based options should be shown
   */
  get shouldShowTimeOptions(): boolean {
    return this.accessType === 'time_based' && this.isAccessTypeEnabled;
  }

  /**
   * Check if invite button should be enabled
   */
  get isInviteButtonEnabled(): boolean {
    if (this.isLoadingShare || !this.isEmailValid) {
      return false;
    }

    // If time-based access is selected, require valid future end date
    if (this.accessType === 'time_based') {
      if (!this.timeBasedEndDate) {
        return false;
      }

      const now = new Date();
      const endDate = new Date(this.timeBasedEndDate);

      // Check if the end date is valid and in the future
      return endDate.getTime() > now.getTime();
    }

    return true;
  }

  /**
   * Check if copy link button should be enabled
   */
  get isCopyLinkButtonEnabled(): boolean {
    if (this.isLoadingShare) {
      return false;
    }

    // If time-based access is selected, require valid future end date
    if (this.accessType === 'time_based') {
      if (!this.timeBasedEndDate) {
        return false;
      }

      const now = new Date();
      const endDate = new Date(this.timeBasedEndDate);

      // Check if the end date is valid and in the future
      return endDate.getTime() > now.getTime();
    }

    return true;
  }

  /**
   * Handle email input changes - reset access type if email is cleared
   */
  onEmailChange(): void {
    if (!this.isEmailValid && this.accessType === 'time_based') {
      // Reset to default access type if email becomes invalid and time was selected
      this.accessType = 'one_time';
      this.timeBasedEndDate = '';
    }
  }

  /**
   * Set access type and handle time-based initialization
   */
  setAccessType(type: 'unlimited' | 'one_time' | 'time_based'): void {
    this.accessType = type;

    // Clear any previously generated link since settings have changed
    this.clearGeneratedLink();

    // Set default 24-hour expiration when time-based is selected
    if (type === 'time_based' && !this.timeBasedEndDate) {
      this.setDefault24HourExpiration();
    }
  }

  /**
   * Set default expiration time to 24 hours from now
   */
  private setDefault24HourExpiration(): void {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours

    // Format for datetime-local input (YYYY-MM-DDTHH:MM)
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    const hours = String(tomorrow.getHours()).padStart(2, '0');
    const minutes = String(tomorrow.getMinutes()).padStart(2, '0');

    this.timeBasedEndDate = `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  /**
   * Calculate duration in seconds when end date changes
   */
  onEndDateChange(): void {
    this.calculateDuration();
  }

  /**
   * Handle access permission changes and clear generated link
   */
  onAccessPermissionChange(): void {
    // Clear any previously generated link since settings have changed
    this.clearGeneratedLink();
  }

  /**
   * Calculate duration based on end date
   */
  private calculateDuration(): void {
    if (!this.timeBasedEndDate) return;

    const now = new Date();
    const endDate = new Date(this.timeBasedEndDate);
    const diffMs = endDate.getTime() - now.getTime();
    this.timeBasedDuration = Math.max(60, Math.floor(diffMs / 1000)); // Minimum 1 minute
  }

  /**
   * Get access type display text
   */
  getAccessTypeDisplay(type: string): string {
    switch (type) {
      case 'unlimited': return 'Unlimited access';
      case 'one_time': return 'One-time view';
      case 'time_based': return 'Time-based access';
      default: return type;
    }
  }

  /**
   * Get status display text
   */
  getStatusDisplay(status: string): string {
    switch (status) {
      case 'not_opened': return 'Not opened';
      case 'used': return 'Used';
      case 'active': return 'Active';
      case 'expired': return 'Expired';
      case 'revoked': return 'Revoked';
      default: return status;
    }
  }

  /**
   * Get simple status class for clean styling
   */
  getStatusClass(share: ShareAccess): string {
    if (share.isRevoked) return 'status-revoked';
    if (share.isExpired) return 'status-expired';
    if (share.isUsed && share.accessType === 'one_time') return 'status-used';
    if (share.accessCount > 0) return 'status-active';
    return 'status-not-opened';
  }

  /**
   * Get status badge color class
   */
  getStatusBadgeClass(share: ShareAccess): string {
    if (share.isRevoked) return 'status-revoked';
    if (share.isExpired) return 'status-expired';
    if (share.isUsed && share.accessType === 'one_time') return 'status-used';
    if (share.accessCount > 0) return 'status-active';
    if (share.emailDelivered === false) return 'status-delivery-failed';
    return 'status-not-opened';
  }

  /**
   * Get comprehensive access information
   */
  getAccessInfo(share: ShareAccess): string {
    let info = this.getAccessTypeDisplay(share.accessType);

    if (share.accessCount > 0) {
      info += ` (${share.accessCount} access${share.accessCount > 1 ? 'es' : ''})`;
    }

    return info;
  }

  /**
   * Get share type display
   */
  getShareTypeDisplay(shareType: string): string {
    switch (shareType) {
      case 'email_share': return 'Email';
      case 'link_share': return 'Link';
      default: return shareType;
    }
  }

  /**
   * Send email invitation
   */
  sendEmailInvitation(): void {
    if (!this.recipientEmail || !this.recipientEmail.includes('@')) {
      swal.fire({
        title: 'Invalid Email',
        text: 'Please enter a valid email address',
        icon: 'error'
      });
      return;
    }

    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    this.isLoadingShare = true;

    const shareRequest = {
      submissionId: this.data.submission.id,
      shareMethod: 'email',
      recipientEmail: this.recipientEmail,
      accessType: this.accessType,
      accessPermission: this.accessPermission,
      ...(this.accessType === 'time_based' && { duration: this.timeBasedDuration }),
      ...(this.shareMessage && { message: this.shareMessage })
    };

    this.http.post<any>(`${environment.apiUrl}/share-report`, shareRequest).subscribe({
      next: (response) => {
        this.isLoadingShare = false;

        // Store the email for success message before clearing
        const invitedEmail = this.recipientEmail;

        // Clear form fields immediately
        this.recipientEmail = '';
        this.accessType = 'one_time'; // Reset to default
        this.timeBasedEndDate = '';
        this.timeBasedDuration = 3600;

        // Reload the shares list to show the new share
        this.loadExistingShares();

        swal.fire({
          title: 'Invitation Sent!',
          text: `An email invitation has been sent to ${invitedEmail}`,
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.isLoadingShare = false;
        console.error('Share error:', error);
        swal.fire({
          title: 'Share Failed',
          text: 'Failed to send invitation. Please try again.',
          icon: 'error'
        });
      }
    });
  }

  /**
   * Generate and copy share link
   */
  generateShareLink(): void {
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    this.isLoadingShare = true;

    const shareRequest = {
      submissionId: this.data.submission.id,
      shareMethod: 'link',
      accessType: this.accessType,
      accessPermission: this.accessPermission,
      ...(this.accessType === 'time_based' && { duration: this.timeBasedDuration })
    };

    this.http.post<any>(`${environment.apiUrl}/share-report`, shareRequest).subscribe({
      next: (response) => {
        this.isLoadingShare = false;

        // Store the generated link
        this.generatedShareLink = response.shareUrl;

        // Reset form state
        this.accessType = 'one_time'; // Reset to default
        this.timeBasedEndDate = '';
        this.timeBasedDuration = 3600;

        // Reload the shares list to show the new share
        this.loadExistingShares();

        // Auto-copy link to clipboard and show success message
        this.copyLinkToClipboard(true);

        swal.fire({
          title: 'Link Generated!',
          text: 'Share link has been generated and copied to clipboard',
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.isLoadingShare = false;
        console.error('Share error:', error);
        swal.fire({
          title: 'Link Generation Failed',
          text: 'Failed to generate share link. Please try again.',
          icon: 'error'
        });
      }
    });
  }

  /**
   * Copy generated link to clipboard
   */
  copyLinkToClipboard(isAutomatic: boolean = false): void {
    if (!this.generatedShareLink) {
      return;
    }

    navigator.clipboard.writeText(this.generatedShareLink).then(() => {
      this.linkCopyMessage = 'Copied!';

      if (!isAutomatic) {
        // Show success message only for manual copy
        swal.fire({
          title: 'Link Copied!',
          text: 'Share link has been copied to clipboard',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }

      // Reset the copy message after 2 seconds
      setTimeout(() => {
        this.linkCopyMessage = '';
      }, 2000);
    }).catch(() => {
      this.linkCopyMessage = 'Copy failed';

      // Fallback for browsers that don't support clipboard API
      swal.fire({
        title: 'Copy Failed',
        text: 'Please manually copy the link below:',
        input: 'text',
        inputValue: this.generatedShareLink,
        inputAttributes: {
          readonly: 'true'
        },
        showCancelButton: false,
        confirmButtonText: 'Close'
      });

      // Reset the error message after 3 seconds
      setTimeout(() => {
        this.linkCopyMessage = '';
      }, 3000);
    });
  }

  /**
   * Remove share access
   */
  removeShareAccess(shareId: string): void {
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    swal.fire({
      title: 'Remove Access?',
      text: 'This will revoke access for this share. Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        // Remove from backend
        this.http.post(`${environment.apiUrl}/shares/${shareId}/revoke`, {}).subscribe({
          next: () => {
            // Reload the shares list to reflect the removal
            this.loadExistingShares();

            swal.fire({
              title: 'Access Removed',
              text: 'Share access has been revoked',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          },
          error: (error) => {
            console.error('Remove share error:', error);
            swal.fire({
              title: 'Removal Failed',
              text: 'Failed to remove access. Please try again.',
              icon: 'error'
            });
          }
        });
      }
    });
  }

  /**
   * Get sharing context display text for debugging/display purposes
   */
  getSharingContextText(): string {
    if (!this.sharingContext) {
      return 'No sharing context available';
    }

    return JSON.stringify(this.sharingContext, null, 2);
  }

  /**
   * Check if current user can perform actions based on sharing context
   */
  canPerformActions(): boolean {
    // Use enhanced security validation
    return this.sessionService.canPerformSensitiveOperation();
  }

  /**
   * Get sharing status message for display
   */
  getSharingStatusMessage(): string {
    if (!this.isSharedSession) {
      return this.translate.instant('sharing.document_not_shared');
    }

    if (this.userRole === 'owner') {
      return this.translate.instant('sharing.you_are_document_owner');
    }

    if (this.userRole === 'recipient') {
      const viewType = this.isViewOnlySession ?
        this.translate.instant('sharing.view_only_access') :
        this.translate.instant('sharing.edit_access');

      let message = `${this.translate.instant('sharing.shared_document')} - ${viewType}`;

      if (this.sharingContext?.sharedBy) {
        message += `\n${this.translate.instant('sharing.shared_by')}: ${this.sharingContext.sharedBy.name} (${this.sharingContext.sharedBy.email})`;
      }

      return message;
    }

    return this.translate.instant('sharing.unknown_sharing_status');
  }

  deleteEverything() {
    this.permissions.deleteInArchive = !this.permissions.deleteInArchive
  }

  cancel() {
    this.dialog.closeAll();
  }

  submit() {
    // Validate security before submitting
    if (!this.sessionService.canPerformSensitiveOperation()) {
      swal.fire({
        title: this.translate.instant('security.operation_denied'),
        text: this.translate.instant('security.insufficient_permissions'),
        icon: 'error'
      });
      return;
    }

    if (this.permissions.deleteEverything) {
      this.permissions.deleteInArchive = true
      // this.permissions.deleteSources = true
    }
    let deleteInArchiveText;
    let deleteEverythingText;
    let deleteSourcesText;
    let title = this.translate.instant('app.are_you_sure_you_want_to_give_student_permission_to');
    if (this.permissions.deleteInArchive && !(this.data.isDocumentInArchive == undefined)) {
      deleteInArchiveText = `<li style="text-align: left;margin-bottom: 20px;"><b>${this.translate.instant('report.delete_document_from_archive')}</b><br/>${this.translate.instant('delete.disables_the_ability_to_detect_plagiarism_in_this_document_from_other_students')}.
      <span style="color:red; display: block; font-weight: bold;font-size: 1rem;margin-left:20px">${this.translate.instant('delete.if_student_deletes_it')}:</span>
      <span style="color:red; display: block; font-weight: bold;font-size: 0.8rem;margin-left:20px">${this.translate.instant('delete.you_will_still_be_able_to_see_everything')}</span></li>`
    } else {
      deleteInArchiveText = ''
    }
    if (this.permissions.deleteEverything) {
      deleteEverythingText = `</br><span style="color:red; display: block; font-weight: bold;font-size: 0.8rem;margin-left:20px">${this.translate.instant('delete.this_document_will_be_permanently_deleted_and_is_irrevertible')}</span>`
    } else {
      deleteEverythingText = ''
    }
    if (this.permissions.deleteSources) {
      deleteSourcesText = `<li style="text-align: left;margin-bottom: 20px;"><b>${this.translate.instant('delete.delete_the_sources')}</b></br>${this.translate.instant('delete.removes_any_source_that_we_found_that_has_similarities_with_this_document')}<span style="color:red; display: block; font-weight: bold;font-size: 1rem;margin-left:20px">${this.translate.instant('delete.if_student_deletes_it')}:</span><span style="color:red; display: block; font-weight: bold;font-size: 0.8rem;margin-left:20px">${this.translate.instant('delete.you_will_still_be_able_to_see_the_percentage_of_similarity')}</span></li>`
    } else {
      deleteSourcesText = ''
    }
    if (!deleteInArchiveText && !deleteSourcesText && !deleteEverythingText) {
      title = this.translate.instant('report.student_wont_be_able_to_delete_anything')
    }
    swal.fire({
      title: title,
      html:
        deleteInArchiveText +
        deleteSourcesText +
        deleteEverythingText,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#087f23',
      denyButtonColor: '#c62828',
      confirmButtonText: this.translate.instant('general.yes'),
      denyButtonText: this.translate.instant('general.no'),
    })
      .then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.submissionsService.deletionPermissions(this.data.submission.id, this.permissions)
            .pipe()
            .subscribe(
              (data: any) => {
                this.spinner.hide();
                swal.fire('', this.translate.instant('delete.permissions_changed_successfully'), 'info');
              },
              (error) => {
                console.log('error', error);
                this.spinner.hide();
              }
            );
        }
      });
  }

  deleteDocument(type) {

    let title;
    let paragraph;

    let deleteText;
    if (type == 0) {
      title = this.translate.instant('delete.all_the_sources_we_found_similarity_will_be_removed')

      deleteText = `<span style="color:red; display: block; font-weight: bold;font-size: 1rem;margin-left:20px">${this.translate.instant('delete.this_is_permanent')}</span>`,
        paragraph = this.translate.instant('delete.sources_deleted_successfully')
      if (this.data.sourcesAreDeleted) {
        swal.fire(this.translate.instant('app.sources_of_this_document_are_already_deleted'), '', 'info');
        return true;
      }
    } else if (type == 1) {
      title = this.translate.instant('delete.your_document_will_be_removed_from_our_archive');
      deleteText = `${this.translate.instant('delete.after_you_delete_this_document_in_our_archives_we_wont_be_able_to_detect_it')} <span style="color:red; display: block; font-weight: bold;font-size: 1rem;margin-left:20px">This is permanent - you will not be able to revert this</span>`
      paragraph = this.translate.instant('delete.document_deleted_successfully_in_our_archive')
      if (this.data.isDocumentInArchive == false) {
        let info: string = "This document is already deleted from our archive"
        swal.fire(info, '', 'info');
        return true;
      }

    } else if (type == 2) {
      title = this.translate.instant('delete.the_document_will_be_deleted_permanently');
      deleteText = `<span style="color:red; display: block; font-weight: bold;font-size: 1rem;margin-left:20px">${this.translate.instant('delete.this_is_permanent')}</span>`
      paragraph = this.translate.instant('delete.document_has_been_deleted')
    }
    swal.fire({
      title: title,
      showDenyButton: true,
      confirmButtonText: this.translate.instant('general.yes'),
      confirmButtonColor: '#087f23',
      denyButtonText: this.translate.instant('general.no'),
      denyButtonColor: '#c62828',
      icon: 'warning',
      html: deleteText
    })
      .then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.submissionsService.deleteSubmissionInElastic(this.data.submission.id, type)
            .pipe()
            .subscribe(
              (data: any) => {
                this.spinner.hide();
                swal.close()
                swal.fire('', `${paragraph}`, 'info').then((result) => {
                  if (result.isConfirmed) {
                    if (this.loggedInUser.roleId === 10) {
                      window.location.replace(`${environment.ioApplicationUrl}professor/submissions`)
                    }

                    if (this.loggedInUser.roleId === 9) {
                      window.location.replace(`${environment.ioApplicationUrl}student/submissions`)
                    }
                  }
                })
              },
              (error) => {
                console.log('error', error);
                this.spinner.hide();
              }
            );
        }
      });

  }

  /**
   * Get duration preview in human-readable format
   */
  get durationPreview(): string {
    if (!this.timeBasedEndDate) {
      return '';
    }

    const now = new Date();
    const endDate = new Date(this.timeBasedEndDate);
    const diffMs = endDate.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'Invalid date (must be in the future)';
    }

    return this.formatDuration(diffMs);
  }

  /**
   * Format duration in milliseconds to human-readable format
   */
  private formatDuration(durationMs: number): string {
    const totalSeconds = Math.floor(durationMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }

    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }

    if (minutes > 0 && days === 0) { // Don't show minutes if we have days
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }

    // For very short durations (less than 1 minute), show seconds
    if (totalMinutes === 0 && seconds > 0) {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return 'Less than 1 second';
    }

    // Join with "and" for the last part
    if (parts.length > 1) {
      const lastPart = parts.pop();
      return parts.join(', ') + ' and ' + lastPart;
    }

    return parts[0];
  }

  /**
   * Toggle row expansion (accordion-style - only one row expanded at a time)
   */
  toggleRowExpansion(shareId: string): void {
    if (this.expandedRows.has(shareId)) {
      // If clicking on the already expanded row, collapse it
      this.expandedRows.delete(shareId);
    } else {
      // Collapse all other rows and expand this one
      this.expandedRows.clear();
      this.expandedRows.add(shareId);
    }
  }

  /**
   * Check if row is expanded
   */
  isRowExpanded(shareId: string): boolean {
    return this.expandedRows.has(shareId);
  }

  /**
   * Toggle sharer expansion (for grouping shares by user)
   */
  toggleSharerExpansion(sharerId: string): void {
    if (this.expandedSharers.has(sharerId)) {
      this.expandedSharers.delete(sharerId);
    } else {
      this.expandedSharers.clear(); // Only allow one expanded at a time
      this.expandedSharers.add(sharerId);
    }
  }

  /**
   * Check if a sharer row is expanded
   */
  isSharerExpanded(sharerId: string): boolean {
    return this.expandedSharers.has(sharerId);
  }

  /**
   * Toggle the others with access table visibility
   */
  toggleOthersTable(): void {
    this.isOthersTableExpanded = !this.isOthersTableExpanded;
  }

  /**
   * Clear the generated link and related messages
   */
  private clearGeneratedLink(): void {
    this.generatedShareLink = '';
    this.linkCopyMessage = '';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) {
      return 'Never';
    }

    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  /**
   * Get share type display text
   * Note: For received shares, this will always be 'Email Share' as the
   * /user/shares/received endpoint now only returns email shares
   */
  getShareTypeDisplayText(shareType: string): string {
    switch (shareType) {
      case 'email_share':
        return 'Email Share';
      case 'link_share':
        return 'Link Share';
      default:
        return shareType;
    }
  }

  /**
   * Get access description for other shares
   */
  getAccessDescription(share: OtherShareInfo): string {
    const sharedDate = this.formatDate(share.shareCreatedAt);

    if (share.accessType === 'time_based') {
      return `${sharedDate} (Time-based)`;
    } else if (share.accessType === 'unlimited') {
      return `Since ${sharedDate} (Unlimited)`;
    } else if (share.accessType === 'one_time') {
      return `${sharedDate} (One-time access)`;
    }

    return `${sharedDate} (${share.accessType})`;
  }

  /**
   * Get the latest share date from a group of shares
   */
  getLatestShareDate(shares: OtherShareInfo[]): string {
    if (!shares || shares.length === 0) {
      return 'N/A';
    }

    const latestShare = shares.reduce((latest, current) => {
      const latestDate = new Date(latest.shareCreatedAt);
      const currentDate = new Date(current.shareCreatedAt);
      return currentDate > latestDate ? current : latest;
    });

    return this.formatDate(latestShare.shareCreatedAt);
  }

  /**
   * Get current share access description for recipients
   */
  getCurrentShareAccessDescription(): string {
    if (!this.currentShareDetails) {
      return 'No share details available';
    }

    const share = this.currentShareDetails.share;
    const sharedDate = this.formatDate(share.shareCreatedAt);

    let description = `Shared by ${share.sharedBy.name} on ${sharedDate}`;

    if (share.accessType === 'time_based' && share.expiresAt) {
      const expiryDate = this.formatDate(share.expiresAt);
      description += ` - Expires: ${expiryDate}`;

      if (share.status === 'expired') {
        description += ' (EXPIRED)';
      }
    } else if (share.accessType === 'unlimited') {
      description += ' (Unlimited access)';
    } else if (share.accessType === 'one_time') {
      description += ' (One-time access)';

      if (share.status === 'used') {
        description += ' - USED';
      }
    }

    return description;
  }

}


export class DialogContentExampleDialog { }
